import { useRef, useState } from 'react'
import { parseExcel, type ImportRow } from '../../lib/excel-import'
import { db } from '../../lib/db'
import { saveExpense } from '../../lib/sync'
import { isTauri } from '../../lib/platform'
import ImportPreview from './ImportPreview'

type Step = 'idle' | 'preview' | 'done'

export default function ImportSection() {
  const fileRef = useRef<HTMLInputElement>(null)
  const [step, setStep]     = useState<Step>('idle')
  const [rows, setRows]     = useState<ImportRow[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [statsStart, setStatsStart] = useState<string | null>(null)
  const [imported, setImported] = useState(0)

  async function handleFile(buffer: ArrayBuffer) {
    const existing = await db.expenses.toArray()
    const result = parseExcel(buffer, existing)
    setRows(result.rows)
    setErrors(result.errors)
    setStatsStart(result.statsStartMonth)
    setStep('preview')
  }

  async function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    handleFile(await file.arrayBuffer())
  }

  async function handlePickTauri() {
    const { open } = await import('@tauri-apps/plugin-dialog')
    const { readFile } = await import('@tauri-apps/plugin-fs')
    const path = await open({ filters: [{ name: 'Excel', extensions: ['xlsx'] }] })
    if (!path || typeof path !== 'string') return
    const data = await readFile(path)
    handleFile(data.buffer as ArrayBuffer)
  }

  async function handleConfirm(selected: ImportRow[]) {
    const now = new Date().toISOString()
    for (const { expense } of selected) {
      await saveExpense({ ...expense, created_at: now, updated_at: now, deleted_at: null })
    }
    // 寫入統計起始月
    if (statsStart) {
      const cur = await db.settings.get('singleton')
      await db.settings.put({
        id: 'singleton',
        stats_start_month: statsStart,
        default_fuel_grade: cur?.default_fuel_grade ?? '95',
      })
    }
    setImported(selected.length)
    setStep('done')
  }

  if (step === 'preview') {
    return (
      <ImportPreview
        rows={rows} errors={errors} statsStartMonth={statsStart}
        onConfirm={handleConfirm}
        onCancel={() => setStep('idle')}
      />
    )
  }

  if (step === 'done') {
    return (
      <div className="import-done">
        <p className="status-msg success">已匯入 {imported} 筆紀錄</p>
        <button className="card-btn" onClick={() => setStep('idle')}>關閉</button>
      </div>
    )
  }

  return (
    <div className="field">
      <label>匯入 Excel</label>
      {isTauri() ? (
        <button type="button" className="card-btn" onClick={handlePickTauri}>
          選擇 .xlsx 檔案…
        </button>
      ) : (
        <>
          <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }}
            onChange={handleInputChange} />
          <button type="button" className="card-btn" onClick={() => fileRef.current?.click()}>
            選擇 .xlsx 檔案…
          </button>
        </>
      )}
    </div>
  )
}
